import { ChangeEvent } from "react";
import { useTranslations } from "next-intl";
import { Field } from "../../forms/field";
import { SelectInput, SelectOption } from "../../forms/select";
import Icon from "../../shared/ui/icon";
import { EntrepriseCustomDataSetting } from "../../../types/models/entreprise-custom-data-setting";
import { EntrepriseCustomDataSettingType } from "../../../types/records/entreprise-custom-data-type-record";

export const CustomDataValues = ({
  settings,
  values,
  onChange,
  readOnly = false,
  titre,
}: {
  settings: EntrepriseCustomDataSetting[] | undefined;
  values: Record<string, string>;
  onChange?: (values: Record<string, string>) => void;
  readOnly?: boolean;
  titre?: string;
}) => {
  const t = useTranslations("custom_data");

  if (!settings || settings.length === 0) return null;

  const heading = titre ?? t("section_title");

  const handleChange = (settingId: number, value: string) => {
    onChange?.({ ...values, [settingId]: value });
  };

  /**
   * Options proposées pour un champ liste. Une valeur déjà enregistrée qui ne figure plus parmi
   * elles est réinjectée en tête, désactivée : retirer une option est permis et ne doit pas faire
   * disparaître silencieusement ce qui avait été saisi.
   */
  const optionsDuChamp = (
    setting: EntrepriseCustomDataSetting,
    valeur: string,
  ): SelectOption[] => {
    const proposees = setting.options ?? [];
    // Rien ne rend un champ personnalisé obligatoire : il doit rester désélectionnable.
    const options: SelectOption[] = [
      { value: "", label: t("no_value") },
      ...proposees.map((option) => ({ value: option, label: option })),
    ];

    if (valeur && !proposees.includes(valeur)) {
      options.splice(1, 0, {
        value: valeur,
        label: valeur,
        disabled: true,
        hint: t("removed_option"),
      });
    }

    return options;
  };

  const inputProps = (setting: EntrepriseCustomDataSetting) => {
    const key = String(setting.id);
    const value = values?.[key] ?? "";

    if (readOnly) {
      if (setting.type === EntrepriseCustomDataSettingType.Boolean) {
        return {
          type: "checkbox",
          checked: value === "true",
          readOnly: true,
        };
      }

      return {
        type:
          setting.type === EntrepriseCustomDataSettingType.Date
            ? "date"
            : setting.type === EntrepriseCustomDataSettingType.Number
              ? "number"
              : "text",
        icon:
          setting.type === EntrepriseCustomDataSettingType.Email
            ? "mail"
            : setting.type === EntrepriseCustomDataSettingType.PhoneNumber
              ? "phone"
              : undefined,
        value,
        readOnly: true,
      };
    }

    switch (setting.type) {
      case EntrepriseCustomDataSettingType.Boolean:
        return {
          type: "checkbox",
          checked: value === "true",
          onChange: (e: ChangeEvent<HTMLInputElement>) =>
            handleChange(setting.id, String(e.target.checked)),
        };
      case EntrepriseCustomDataSettingType.Date:
        return {
          type: "date",
          value,
          onChange: (e: ChangeEvent<HTMLInputElement>) =>
            handleChange(setting.id, e.target.value),
        };
      case EntrepriseCustomDataSettingType.Number:
        return {
          type: "number",
          value,
          onChange: (e: ChangeEvent<HTMLInputElement>) =>
            handleChange(setting.id, e.target.value),
        };
      case EntrepriseCustomDataSettingType.Email:
        return {
          type: "email",
          icon: "mail",
          value,
          onChange: (e: ChangeEvent<HTMLInputElement>) =>
            handleChange(setting.id, e.target.value),
        };
      case EntrepriseCustomDataSettingType.PhoneNumber:
        return {
          type: "tel",
          icon: "phone",
          value,
          onChange: (e: ChangeEvent<HTMLInputElement>) =>
            handleChange(setting.id, e.target.value),
        };
      default:
        return {
          type: "text",
          value,
          onChange: (e: ChangeEvent<HTMLInputElement>) =>
            handleChange(setting.id, e.target.value),
        };
    }
  };

  return (
    <div className="px-4 py-4 sm:px-6">
      <h4 className="text-[11px] font-bold text-slate-400 dark:text-white uppercase tracking-widest mb-4 flex items-center gap-2">
        <Icon name="description" className="text-white text-sm" />
        {heading}
      </h4>
      <div className="grid grid-cols-2 gap-4 items-end">
        {settings.map((setting) => {
          // En lecture seule, la valeur stockée est déjà le libellé de l'option : le rendu texte
          // commun convient, inutile de sortir un menu déroulant.
          // Repli sur un champ texte si le champ ne porte aucune option : un menu déroulant vide
          // serait inutilisable, et c'est ce que renverrait un back-end pas encore déployé.
          if (
            setting.type === EntrepriseCustomDataSettingType.List &&
            !readOnly &&
            (setting.options?.length ?? 0) > 0
          ) {
            const valeur = values?.[String(setting.id)] ?? "";

            return (
              <SelectInput
                key={setting.id}
                titre={setting.name}
                containerClassName="col-span-2 md:col-span-1"
                placeholder="Sélectionner une valeur"
                options={optionsDuChamp(setting, valeur)}
                value={valeur}
                onChange={(choix) => handleChange(setting.id, String(choix))}
              />
            );
          }

          return (
            <Field
              key={setting.id}
              titre={setting.name}
              containerClassName="col-span-2 md:col-span-1"
              {...inputProps(setting)}
            />
          );
        })}
      </div>
    </div>
  );
};
